import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCcw, CheckCircle2, Loader2, Calendar, Trash2, Pencil, ImageDown } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import StatusBadge from "@/components/StatusBadge";
import AnimalTypeIcon from "@/components/AnimalTypeIcon";
import AdminCaseSettings from "@/components/AdminCaseSettings";
import SanctuaryInfoSection from "@/components/SanctuaryInfoSection";
import CasePhotoGallery from "@/components/case-detail/CasePhotoGallery";
import DonationSummaryCard from "@/components/case-detail/DonationSummaryCard";
import CaseDonationsList from "@/components/case-detail/CaseDonationsList";
import TreatmentUpdatesSection from "@/components/case-detail/TreatmentUpdatesSection";
import StatusTimelineSection from "@/components/case-detail/StatusTimelineSection";
import StatusChangeDialog from "@/components/dialogs/StatusChangeDialog";
import AddUpdateDialog from "@/components/dialogs/AddUpdateDialog";
import EditCaseDialog from "@/components/dialogs/EditCaseDialog";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import api, { getCasePhotoUrls, buildShareUrl } from "@/lib/api";
import { isDriveFolderLink } from "@/lib/driveUtils";
import { formatDate, daysUnderCareLabel } from "@/lib/dateUtils";
import { getAnimalTypeLabel } from "@/lib/animalTypes";
import { generateStoryImage, downloadBlob } from "@/lib/storyImageGenerator";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();

  const [caseItem, setCaseItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [downloadingStory, setDownloadingStory] = useState(false);

  const showSuccess = searchParams.get("created") === "1";

  const fetchCase = useCallback(async () => {
    const { data } = await api.get(`/cases/${id}`);
    setCaseItem(data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const handleDelete = async () => {
    await api.delete(`/cases/${id}`);
    toast({ title: "Case deleted" });
    navigate("/dashboard");
  };

  const handleDownloadStory = async () => {
    setDownloadingStory(true);
    try {
      const cover = getCasePhotoUrls(caseItem)[0];
      const pageUrl = buildShareUrl(caseItem.slug);
      const blob = await generateStoryImage(caseItem, cover, pageUrl);
      if (!blob) throw new Error("blob generation failed");
      downloadBlob(blob, `${caseItem.slug}-story.png`);
      toast({ title: "Story image downloaded" });
    } catch {
      toast({ variant: "destructive", title: "Could not create story image", description: "Please try again." });
    } finally {
      setDownloadingStory(false);
    }
  };

  if (loading || !caseItem) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <StaffHeader />
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-amtm-teal" />
        </div>
      </div>
    );
  }

  const allPhotos = getCasePhotoUrls(caseItem);
  const showDriveFolderLink = isDriveFolderLink(caseItem.external_photo_link);
  const sortedUpdates = [...caseItem.treatment_updates].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        <button onClick={() => navigate("/dashboard")} data-testid="case-detail-back-button" className="flex items-center gap-1 text-sm text-amtm-textMuted mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        {showSuccess && (
          <div className="flex items-center gap-2 bg-amtm-success/15 text-amtm-success rounded-xl p-4 mb-4" data-testid="case-created-success-banner">
            <CheckCircle2 className="h-5 w-5" /> Case saved successfully!
          </div>
        )}

        <CasePhotoGallery
          photos={allPhotos}
          showDriveFolderLink={showDriveFolderLink}
          driveLink={caseItem.external_photo_link}
          animalName={caseItem.animal_name}
          animalType={caseItem.animal_type}
        />

        <div className="flex items-center justify-between mb-2 gap-2">
          <h1 className="font-display text-3xl text-amtm-text" data-testid="case-detail-name">
            #{caseItem.case_number} {caseItem.animal_name}
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={caseItem.status} />
            <button onClick={() => setEditDialogOpen(true)} data-testid="edit-case-button" className="p-1.5 rounded-full text-amtm-textMuted hover:text-amtm-teal hover:bg-amtm-tealTint">
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-amtm-textMuted mb-4">
          <span className="flex items-center gap-1"><AnimalTypeIcon animalType={caseItem.animal_type} className="h-4 w-4" /> {getAnimalTypeLabel(caseItem)}</span>
          <span>{caseItem.breed}</span>
          <span>{caseItem.color}</span>
          <span className="flex items-center gap-1" data-testid="case-detail-admission-date">
            <Calendar className="h-4 w-4" /> {caseItem.admission_date ? `Admitted ${formatDate(caseItem.admission_date)}` : "Admission date missing"}
          </span>
          <span data-testid="case-detail-days-label">{daysUnderCareLabel(caseItem)}</span>
        </div>

        <p className="text-amtm-text mb-6" data-testid="case-detail-history">{caseItem.case_history}</p>

        <SanctuaryInfoSection caseItem={caseItem} />

        <DonationSummaryCard
          caseId={id}
          donationNeeded={caseItem.donation_needed}
          donationReceived={caseItem.donation_received_total ?? caseItem.donation_received}
          onUpdated={fetchCase}
        />
        <CaseDonationsList caseId={id} />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <Button onClick={() => setUpdateDialogOpen(true)} data-testid="add-update-button" className="h-14 text-base font-semibold bg-amtm-info text-white hover:bg-amtm-info/90 rounded-xl">
            <Plus className="h-5 w-5 mr-1.5" /> Add Update
          </Button>
          <Button onClick={() => setStatusDialogOpen(true)} data-testid="change-status-button" className="h-14 text-base font-semibold bg-amtm-teal text-white hover:bg-amtm-tealHover rounded-xl">
            <RefreshCcw className="h-5 w-5 mr-1.5" /> Change Status
          </Button>
        </div>

        <Button
          onClick={handleDownloadStory}
          disabled={downloadingStory}
          variant="outline"
          className="w-full h-11 mb-8 border-amtm-teal text-amtm-teal hover:bg-amtm-tealTint"
          data-testid="download-story-image-button"
        >
          {downloadingStory ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImageDown className="h-4 w-4 mr-1.5" />}
          Download Story Image
        </Button>

        {user?.role === "admin" && (
          <div className="mb-8">
            <AdminCaseSettings caseItem={caseItem} onUpdated={fetchCase} />
          </div>
        )}

        <TreatmentUpdatesSection updates={sortedUpdates} caseId={id} onUpdated={fetchCase} />
        <StatusTimelineSection history={caseItem.status_history} caseId={id} onUpdated={fetchCase} />

        {user?.role === "admin" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full border-amtm-error text-amtm-error hover:bg-amtm-error/10" data-testid="delete-case-button">
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete Case
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-case-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this case permanently?</AlertDialogTitle>
                <AlertDialogDescription>This will remove {caseItem.animal_name}'s case and all its history. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-case-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} data-testid="delete-case-confirm" className="bg-amtm-error hover:bg-amtm-error/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <StatusChangeDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen} caseItem={caseItem} onUpdated={fetchCase} />
      <AddUpdateDialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen} caseId={id} onUpdated={fetchCase} />
      <EditCaseDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} caseItem={caseItem} onUpdated={fetchCase} />
    </div>
  );
}
